import { Skeleton } from 'antd'
import dayjs from 'dayjs'
import { useEffect, useMemo, useState } from 'react'

import { API } from '@/api'
import { PersonalInstanceStat } from '@/api/type-response'
import { ServerDomainInfo, Volume } from '@/api/type-server'
import { Trans } from '@/components'
import { Instance } from '@/models/instance'
import { VOLUME_TYPE } from '@/models/volume'
import ExpansionModal from '@/pages/space-mgr/components/expansion-modal'
import {
  getDatadriveGradientClass,
  VolumeUsageSegmentedBar,
} from '@/pages/space-mgr/components/volume-usage-segmented-bar'
import { useTeamStore } from '@/stores/team'
import { useUserStore } from '@/stores/user'
import { formatDomain, formatPrice, formatToDynamicDecimalPlaces } from '@/utils/formatter'
import { getStorageBillingMode } from '@/utils/storage-billing'
import { cn } from '@/utils/style'

import { CostRankingTable } from './components/CostRankingTable'
import { TeamInfoHeader } from './components/TeamInfoHeader'

export function MemberHomeView() {
  const [snapshotVolumes, setSnapshotVolumes] = useState<Volume[]>([])
  const [cloudVolumes, setCloudVolumes] = useState<Volume[]>([])
  const [imageVolumes, setImageVolumes] = useState<Volume[]>([])
  const [expansionModalOpen, setExpansionModalOpen] = useState(false)
  const [personalInstanceStat, setPersonalInstanceStat] = useState<PersonalInstanceStat | null>(
    null,
  )
  const [statsLoading, setStatsLoading] = useState(true)
  const [instances, setInstances] = useState<Instance[]>([])
  const [domainInfos, setDomainInfos] = useState<ServerDomainInfo[]>([])

  const [imageOnSpaceBilling, setImageOnSpaceBilling] = useState(true)
  const [snapshotOnSpaceBilling, setSnapshotOnSpaceBilling] = useState(true)
  const bothSwitched = !imageOnSpaceBilling && !snapshotOnSpaceBilling

  const teamStore = useTeamStore()
  const userStore = useUserStore()
  const account = useUserStore((state) => state.account)

  useEffect(() => {
    loadData()
  }, [])

  const loadData = async () => {
    setStatsLoading(true)
    try {
      const teamResID = teamStore.teamTokenData?.teamResID

      // Parallel API calls for better performance
      const [volumeRes, instancesRes, domainRes, statsRes] = await Promise.all([
        API.getVolumes(),
        API.getNodeList({
          page: 1,
          per_page: 100, // Get enough instances for personal use
          region_filter_criteria: 0,
        }),
        API.getDomainList(),
        teamResID ? API.getTeamStatPersonalInstances({ res_id: teamResID }) : null,
      ])

      // Handle volumes
      if (volumeRes?.code === 0) {
        const volumes = volumeRes?.volumes || []
        setSnapshotVolumes(volumes.filter((v) => v.type === VOLUME_TYPE.SNAPSHOT))
        setCloudVolumes(volumes.filter((v) => v.type === VOLUME_TYPE.DATA_DRIVE))
        setImageVolumes(volumes.filter((v) => v.type === VOLUME_TYPE.IMAGE))

        const mode = getStorageBillingMode(volumes)
        setImageOnSpaceBilling(mode.imageOnSpaceBilling)
        setSnapshotOnSpaceBilling(mode.snapshotOnSpaceBilling)
      }

      // Handle instances (for cost ranking)
      if (instancesRes?.code === 0) {
        const instanceList = instancesRes.userNodes.map((data) => new Instance(data))
        setInstances(instanceList)
      }

      // Handle domain infos
      if (domainRes.code === 0) {
        setDomainInfos(domainRes.domainInfos || [])
      }

      // Handle personal instance stats
      if (statsRes) {
        setPersonalInstanceStat(statsRes.personalInstanceStat)
      }

      await Promise.all([userStore.getVolume(), userStore.getOrder()])
    } catch (error) {
      console.error('Failed to load data:', error)
    } finally {
      setStatsLoading(false)
    }
  }

  // Calculate quotas and usage (same as MemberSpaceView)
  const allocatedQuotaUsageInGB = (userStore.partVolume?.usage_mb || 0) / 1024
  const partVolumeQuotaInGB = userStore.isOrderExpired
    ? userStore.partVolume?.last_quota_gb || 0
    : userStore.partVolume?.quota_gb || 0

  // Calculate total actual usage from all volumes
  const totalActualUsageInGB = useMemo(() => {
    const allVolumes = [
      ...(snapshotVolumes || []),
      ...(cloudVolumes || []),
      ...(imageVolumes || []),
    ]
    return allVolumes.reduce((sum, volume) => sum + volume.usage_mb / 1024, 0)
  }, [snapshotVolumes, cloudVolumes, imageVolumes])

  return (
    <div className="h-full overflow-y-auto flex flex-col gap-2.5">
      {/* Team Info Header */}
      <TeamInfoHeader />
      <div className="bg-panel-white p-8 rounded-[32px] h-full overflow-y-auto">
        {/* Stats Cards */}
        <div className="bg-panel-white grid grid-cols-3 gap-4 mb-4">
          <div className="p-6 bg-panel-white rounded-2xl border border-neutral-200 flex flex-col gap-y-2">
            <div className="t-x-sb text-secondary">
              <Trans i18nKey="teamHome.member.stats.balanceCredit">Balance (Credit)</Trans>
            </div>
            <div className="h5">{formatPrice(account.totalBonus.moneyCent ?? 0, false, '')}</div>
          </div>
          <div className="p-6 bg-panel-white rounded-2xl border border-neutral-200 flex flex-col gap-y-2">
            <div className="t-x-sb text-secondary">
              <Trans i18nKey="teamHome.member.stats.totalSpendingCredit">
                Total Spending (Credit)
              </Trans>
            </div>
            <div className="h5"> {formatPrice(account.expense.amount ?? 0, false, '')}</div>
          </div>
          <div className="p-6 bg-panel-white rounded-2xl border border-neutral-200 flex flex-col gap-y-2">
            <div className="t-x-sb text-secondary">
              <Trans i18nKey="teamHome.member.stats.instances">Instances</Trans>
            </div>
            <div className="h5 flex items-center gap-1">
              {statsLoading ? (
                <Skeleton.Input active size="small" />
              ) : (
                <>
                  <span>{personalInstanceStat?.unfinishedInstanceNum ?? 0}</span>
                  <span className="text-tertiary">/</span>
                  <span className="text-tertiary">
                    {personalInstanceStat?.unfinishedInstanceLimit ?? 0}
                  </span>
                </>
              )}
            </div>
          </div>
        </div>

        {/* Storage Space - Same as MemberSpaceView */}
        <div className="bg-panel-white rounded-2xl p-8 border border-neutral-200 mb-4">
          <div className="flex items-center justify-between">
            <div className="t-sb">
              <Trans i18nKey="space.titleSpaceStorage">Storage Space</Trans>
            </div>
          </div>

          <div className="my-4">
            <VolumeUsageSegmentedBar
              snapshotVolumes={snapshotVolumes}
              cloudVolumes={cloudVolumes}
              imageOnSpaceBilling={imageOnSpaceBilling}
              snapshotOnSpaceBilling={snapshotOnSpaceBilling}
            />
          </div>

          <div className="flex items-center justify-between">
            <div className="flex gap-4 items-center">
              <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
                <div className="text-primary t-sb">
                  <Trans
                    i18nKey="space.usageFormat"
                    values={{
                      usage: formatToDynamicDecimalPlaces(totalActualUsageInGB),
                      quota: formatToDynamicDecimalPlaces(partVolumeQuotaInGB),
                    }}
                    components={[
                      <span className="text-primary" key="usage" />,
                      <span className="text-primary" key="quota" />,
                    ]}
                  />
                </div>
              </div>

              {userStore.order && (
                <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
                  <div className="text-primary t-sb flex items-center space-x-1">
                    <div className="text-secondary">
                      <Trans i18nKey="space.expireOn">Expires on</Trans>
                    </div>
                    <div>
                      {userStore.order?.deadlineTime?.seconds ? (
                        dayjs(userStore.order.deadlineTime.seconds * 1000).format('YYYY-MM-DD')
                      ) : (
                        <Skeleton.Input active size="small" style={{ width: 100 }} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              {userStore.isOrderExpired && (
                <div className="relative pr-4 after:content-[''] after:absolute after:right-0 after:top-1/2 after:transform after:-translate-y-1/2 after:w-[2px] after:h-[12px] after:bg-neutral-200">
                  <div className="text-primary t-sb flex items-center space-x-1">
                    <div className="text-secondary">
                      <Trans i18nKey="teamHome.member.storage.dataPurgeOn">Data Purge on</Trans>
                    </div>
                    <div className="text-error">
                      {userStore.order?.extra?.purgeTime?.seconds ? (
                        dayjs(userStore.order.extra.purgeTime.seconds * 1000).format('YYYY-MM-DD')
                      ) : (
                        <Skeleton.Input active size="small" style={{ width: 100 }} />
                      )}
                    </div>
                  </div>
                </div>
              )}

              <div
                className="text-btn-secondary font-bold cursor-pointer t-sb"
                onClick={() => setExpansionModalOpen(true)}
              >
                <Trans i18nKey="space.upgrade">Upgrade</Trans>
              </div>
            </div>
            {bothSwitched
              ? (cloudVolumes || []).some((vol) => (vol.quota_gb || vol.last_quota_gb) > 0) && (
                  <div>
                    <div className="flex items-baseline t-sm text-primary gap-2">
                      {(cloudVolumes || []).map((volume, index) => (
                        <div
                          key={`legend.${volume.id}.${index}`}
                          className="flex items-baseline gap-2"
                        >
                          <div
                            className={cn(
                              'w-3 h-3',
                              index > 0 && 'ml-2',
                              getDatadriveGradientClass(index, userStore.isOrderExpired),
                            )}
                          />
                          {domainInfos.find((d) => d.domain === volume.domain)?.displayName ||
                            formatDomain(volume.domain)}
                        </div>
                      ))}
                      <div className="w-3 h-3 ml-2 bg-neutral-200" />
                      <Trans i18nKey="space.legendFreeSpace">Free Space</Trans>
                    </div>
                  </div>
                )
              : allocatedQuotaUsageInGB > 0 && (
                  <div>
                    <div className="flex items-baseline t-sm text-primary gap-2">
                      {snapshotOnSpaceBilling && (
                        <>
                          <div
                            className={cn(
                              ' w-3 h-3',
                              userStore.isOrderExpired ? 'bg-bar-tertiary' : ' bg-btn-tertiary',
                            )}
                          />
                          <Trans i18nKey="space.volumeItemSubTitleSnapshot">Snapshot</Trans>
                        </>
                      )}
                      <div
                        className={cn(
                          ' w-3 h-3 ml-2',
                          userStore.isOrderExpired ? 'bg-bar-tertiary/60' : 'bg-btn-secondary',
                        )}
                      />
                      <Trans i18nKey="space.volumeItemSubTitleCloudStorage">Datadrive</Trans>
                      {imageOnSpaceBilling && (
                        <>
                          <div
                            className={cn(
                              ' w-3 h-3 ml-2',
                              userStore.isOrderExpired ? 'bg-tertiary' : 'bg-active',
                            )}
                          />
                          <Trans i18nKey="space.volumeItemSubTitleImage">Image</Trans>
                        </>
                      )}
                    </div>
                  </div>
                )}
          </div>

          {userStore.isOrderExpired && (
            <div className={cn('t-sm ', 'text-error', 'mt-4')}>
              <Trans i18nKey="space.volumeItemExpired">
                Your storage space has expired, please upgrade your plan
              </Trans>
            </div>
          )}
        </div>

        {/* Cost Ranking Table */}
        <CostRankingTable instances={instances} domainInfos={domainInfos} />
      </div>

      {/* Expansion Modal */}
      <ExpansionModal
        open={expansionModalOpen}
        onClose={() => setExpansionModalOpen(false)}
        onOk={async () => {
          setExpansionModalOpen(false)
          await userStore.getUserInfo()
          await loadData()
        }}
      />
    </div>
  )
}
